import { Component, OnInit, OnDestroy } from '@angular/core';
import { ModelService } from '../model.service';
import { Model } from '../../models/model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MediaService } from '../../media/media.service';
// Removed LikesService import per cleanup instructions

@Component({
  selector: 'app-modellist',
  templateUrl: './modellist.component.html',
  styleUrls: ['./modellist.component.css']
})
export class ModelListComponent implements OnInit, OnDestroy {
  models: Model[] = [];
  filteredModels: Model[] = [];
  value = '';
  sortOrder = '';
  imageLoading: { [modelId: string]: boolean } = {};
  dataLoading: boolean = true;
  // Removed likedStates since like toggling is no longer interactive

  private destroy$ = new Subject<void>();

  constructor(private modelService: ModelService, private snackBar: MatSnackBar, private mediaService: MediaService) {}

  ngOnInit(): void {
    this.fetchModels();
  }

  fetchModels() {
    this.dataLoading = true;
    this.modelService.getModels().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        // Sort by name ascending as a default stable order and ensure stat defaults
        this.models = [...data]
          .map(model => {
            model.likesCount = model.likesCount ?? 0;
            model.viewsCount = model.viewsCount ?? 0;
            (model as any).imagesCount = (model as any).imagesCount ?? 0;
            (model as any).videosCount = (model as any).videosCount ?? 0;
            return model;
          })
          .sort((a, b) => a.name.localeCompare(b.name));
        this.filteredModels = [...this.models];
        data.forEach(model => { this.imageLoading[model.model_id] = true; });
        // Batch refresh media counts for all models to maintain consistency
        const modelsById = new Map(this.models.map(m => [m.model_id, m]));
        const countPromises = this.models.map(m => this.mediaService.getUserMediaCounts(m.model_id).toPromise());
        Promise.all(countPromises)
          .then(results => {
            results.forEach((counts, idx) => {
              const m = this.models[idx];
              if (m && counts) {
                (m as any).imagesCount = counts.imagesCount;
                (m as any).videosCount = counts.videosCount;
              }
            });
            // Ensure filteredModels reflects updated counts
            this.filteredModels = [...this.models];
          })
          .catch(() => {})
          .finally(() => { this.dataLoading = false; });
      },
      error: () => {
        this.dataLoading = false;
        this.snackBar.open('Failed to load models', 'Close', { duration: 3000 });
      }
    });
  }
  // Removed toggleLike handler; likes are now displayed as static stats
  onImageLoad(modelId: string) {
    this.imageLoading[modelId] = false;
  }

  onImageError(modelId: string) {
    this.imageLoading[modelId] = false;
  }

  applySearchFilter(event: Event): void {
    let searchTerm = (event.target as HTMLInputElement).value;
    searchTerm = searchTerm.toLowerCase();

    this.filteredModels = this.models.filter((product) =>
      product.name.toLowerCase().includes(searchTerm)
    );

    this.sortModels(this.sortOrder);
  }

  clearSearchFilter() {
    this.value = '';
    this.filteredModels = this.models;
  }

  sortModels(sortCriteria: string) {
    let sortOrder = sortCriteria;

    if (sortOrder === 'nameAscending') {
      this.filteredModels.sort((a, b) => a.name.localeCompare(b.name));
    }else if (sortOrder === 'nameDescending') {
      this.filteredModels.sort((a, b) => b.name.localeCompare(a.name));
    }

  }

  getModelImage(model: Model): string {
    return model.profile_image_url && model.profile_image_url.trim() !== ''
      ? model.profile_image_url
      : (model.gender === 'Male'
        ? 'assets/images/placeholder_male_profile_image.png'
        : 'assets/images/placeholder_profile_image.png');
  }

  trackByModelId(index: number, model: Model) { return model.model_id; }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
